import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { debounceTime, distinctUntilChanged, ReplaySubject, takeUntil } from 'rxjs';
import { ToastrService } from '../../../services/toastr-service';

import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-table';
import { MasterDataManagementService } from '../../../services/master-data-management.service';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { LabourRatesHeader } from '../../../models/labour-rates-header.model';
import { labourRatesListGridColumns } from './labour-rates-grid.config';
import { LabourRatesFilterForm } from '../../../form-model/labour-rates-filter.form.model';
import { LabourRatesFilter, LabourRatesFilterGrid } from '../../../models/labour-rates-filter.model';
import { CodeDescription } from '../../../models/code-description.model';
import { MasterDataAuditLogComponent } from '../../../shared-components/master-data-audit-log/master-data-audit-log.component';
import { AuthPolicies } from '../../../auth/auth-policies';
import { AuthHelper } from '../../../auth/auth-helper';

@Component({
  selector: 'app-labour-rates-grid',
  imports: [NgxSkeletonLoaderModule, RouterModule, LoaderComponent, FormsModule, ReactiveFormsModule, MasterDataAuditLogComponent],
  templateUrl: './labour-rates-grid.component.html',
  styleUrl: './labour-rates-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LabourRatesGridComponent implements OnInit, OnDestroy {
  data: LabourRatesHeader[] = [];
  mockDataForLoadingAnimation: { id: number }[] = [...new Array(30).keys()].map((i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 20;
  sort: string | undefined;
  filterParam: LabourRatesFilter = {};
  getLabourRatesApiCallStatus: APICallStatus = 'NOT_STARTED';
  hasData: boolean = false;
  urlNavigate: string | undefined;
  openAuditLog: boolean = false;
  selectedMreRateNumber: string | null = null;

  @Input({ required: true }) filterFormGroup!: FormGroup<{
    labourRatesFilterForm: FormGroup<LabourRatesFilterForm>;
  }>;

  columns = labourRatesListGridColumns;

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  /** Shop codes assigned to the current user (populated for shop-role users) */
  private readonly shopUserCodes: string[] = AuthPolicies.isGSCUser()
    ? []
    : (AuthHelper.user?.maintenanceShops?.map((s) => s.maintenanceShopCode) ?? []);

  constructor(
    private readonly masterDataManagementService: MasterDataManagementService,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
  ) {}

  ngOnInit() {
    this.getLabourRatesApiCallStatus = 'IN_PROGRESS';
    this.subscribeToFormChanges();
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getValue(row: LabourRatesHeader, columnId: string): string | number {
    const rawValue = row[columnId as keyof LabourRatesHeader] ?? '-';

    // Find the column config to check for a formatter
    const column = this.columns.find((col) => col.id === columnId);
    if (!column?.dataType?.formatter) {
      return rawValue;
    }

    // Apply the formatter if it exists
    try {
      return column.dataType.formatter(rawValue, row as any);
    } catch (error) {
      console.error(`Error formatting column ${columnId}:`, error);
      return rawValue;
    }
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      if (this.filterFormGroup.controls.labourRatesFilterForm.controls.applyLabourRatesFilter.value) {
        this.onApplyFilter();
        this.filterFormGroup.controls.labourRatesFilterForm.controls.applyLabourRatesFilter.setValue(false);
      }
    });
  }

  onApplyFilter() {
    if (this.filterFormGroup.controls.labourRatesFilterForm.valid) {
      const form = this.filterFormGroup.controls.labourRatesFilterForm;
      const rawFilters = form.value;
      const expiryDateType = rawFilters.expiryDateType;
      const effectiveDateType = rawFilters.effectiveDateType;
      let filters: LabourRatesFilter = {
        shopCode: rawFilters.shopCode ?? undefined,
        equipmentType: rawFilters.equipmentType ?? undefined,
        expiryDateType: expiryDateType ?? undefined,
        expiryDate: rawFilters.expiryDate ?? undefined,
        expiryDateFrom: rawFilters.expiryDateFrom ?? undefined,
        expiryDateTo: rawFilters.expiryDateTo ?? undefined,
        effectiveDateType: effectiveDateType ?? undefined,
        effectiveDate: rawFilters.effectiveDate ?? undefined,
        effectiveDateFrom: rawFilters.effectiveDateFrom ?? undefined,
        effectiveDateTo: rawFilters.effectiveDateTo ?? undefined,
      };
      if (expiryDateType === 'BETWEEN') {
        filters.expiryDate = undefined;
      } else {
        filters.expiryDateFrom = undefined;
        filters.expiryDateTo = undefined;
      }
      if (effectiveDateType === 'BETWEEN') {
        filters.effectiveDate = undefined;
      } else {
        filters.effectiveDateFrom = undefined;
        filters.effectiveDateTo = undefined;
      }
      this.filterParam = filters;
      this.currentPage = 1;
      this.getLabourRates();
    }
  }

  onSortDirectionChange(event: Event) {
    let customEvent = event as CustomEvent;
    let sortColumn: string = this.sortColumnName(customEvent.detail.column);
    let sortDirection: string = customEvent.detail.direction == 'ascending' ? 'asc' : 'desc';
    if (sortColumn != 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getLabourRates();
    }
  }
  private sortColumnName(gridColumnName: string): string {
    return gridColumnName;
  }

  onPageLimitChange(event: Event) {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;

    this.getLabourRates();
  }

  onPageChange(event: Event) {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getLabourRates();
    }
  }

  getLabourRates(): void {
    this.data = [];
    this.getLabourRatesApiCallStatus = 'IN_PROGRESS';
    const apiParam = this.buildLabourRateApiParam();
    this.masterDataManagementService
      .getLabourRate(this.currentPage, this.pageLimit, this.sort, apiParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => this.handleLabourRatesResponse(response, apiParam),
        error: () => {
          this.getLabourRatesApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }

  private buildLabourRateApiParam(): LabourRatesFilterGrid {
    const apiFilterParam: LabourRatesFilter = { ...this.filterParam };
    apiFilterParam.shopCode = Array.isArray(apiFilterParam.shopCode) ? apiFilterParam.shopCode : [];
    if (this.shopUserCodes.length > 0 && apiFilterParam.shopCode.length === 0) {
      apiFilterParam.shopCode = this.shopUserCodes;
    }
    this.cleanupDateFilter(apiFilterParam, 'expiryDate', 'expiryDateType', 'expiryDateFrom', 'expiryDateTo');
    this.cleanupDateFilter(apiFilterParam, 'effectiveDate', 'effectiveDateType', 'effectiveDateFrom', 'effectiveDateTo');
    return {
      ...apiFilterParam,
      equipmentType: Array.isArray(apiFilterParam.equipmentType)
        ? apiFilterParam.equipmentType.filter((et) => typeof et === 'object' && 'code' in et).map((et: CodeDescription) => et.code)
        : undefined,
    };
  }

  private cleanupDateFilter(
    param: LabourRatesFilter,
    dateKey: keyof LabourRatesFilter,
    typeKey: keyof LabourRatesFilter,
    fromKey: keyof LabourRatesFilter,
    toKey: keyof LabourRatesFilter,
  ): void {
    if (param[typeKey] === 'BETWEEN') {
      delete param[dateKey];
      if (!param[fromKey]) delete param[fromKey];
      if (!param[toKey]) delete param[toKey];
    } else {
      delete param[fromKey];
      delete param[toKey];
      if (!param[dateKey]) delete param[dateKey];
    }
  }

  private handleLabourRatesResponse(response: any, apiParam: LabourRatesFilterGrid): void {
    const headers = response.headers;
    const body: LabourRatesHeader[] = response.body || [];

    body.forEach((item) => {
      if (item.effectiveDate) item.effectiveDate = item.effectiveDate.toString().split('T')[0];
      if (item.expiryDate) item.expiryDate = item.expiryDate.toString().split('T')[0];
    });

    this.data = body.map((item, index) => ({
      ...item,
      id: `${item.shopCode}__${item.equipmentType}__${index}`,
    }));
    this.firstPage = headers.get('first-page') ? +headers.get('first-page')! : undefined;
    this.previousPage = headers.get('previous-page') ? +headers.get('previous-page')! : undefined;
    this.currentPage = headers.get('current-page') ? +headers.get('current-page')! : 1;
    this.nextPage = headers.get('next-page') ? +headers.get('next-page')! : undefined;
    this.lastPage = headers.get('last-page') ? +headers.get('last-page')! : undefined;
    this.totalItemCount = headers.get('total-item-count') ? +headers.get('total-item-count')! : undefined;
    this.apiVersion = headers.get('api-version') ?? undefined;
    this.getLabourRatesApiCallStatus = 'COMPLETED_SUCCESFULLY';
    if (this.data.length > 0) {
      this.masterDataManagementService.filterData = apiParam;
    }
  }

  clearFilter() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.labourRatesFilterForm.controls.applyLabourRatesFilter.setValue(true);
  }

  onAuditLogClicked(row: LabourRatesHeader) {
    this.selectedMreRateNumber = row.mreRateNumber;
    if (!this.selectedMreRateNumber) {
      this.toastrService.showToastr('MRE rate number not available for audit log', 'error');
      return;
    }

    this.openAuditLog = true;
  }

  onAuditLogClosed() {
    // Implement audit log close handling logic here
    this.openAuditLog = false;
  }

  readonly auditLogFetcher = this.masterDataManagementService.getLabourRateAuditLogs.bind(this.masterDataManagementService);
}
